<template>
  <div class="so-wrap">
    <!-- Traffic & behaviour (Cloudflare + Microsoft Clarity) -->
    <div class="so-full">
      <TrafficCard :site-id="siteId" />
    </div>

    <!-- Google Analytics (GA4) -->
    <section class="so-panel">
      <div class="so-panel-head">
        <div class="so-panel-label">Google Analytics</div>
        <span class="so-panel-sub">GA4 — last 30 days</span>
      </div>
      <p v-if="gaLoading" class="so-muted">Loading GA4…</p>
      <template v-else-if="ga && ga.status === 'ok'">
        <div class="so-metrics-row">
          <div class="so-metric"><span class="so-metric-val">{{ fmt(ga.metrics.sessions) }}</span><span class="so-metric-label">Sessions</span></div>
          <div class="so-metric"><span class="so-metric-val">{{ fmt(ga.metrics.users) }}</span><span class="so-metric-label">Users</span></div>
          <div class="so-metric"><span class="so-metric-val">{{ fmt(ga.metrics.pageviews) }}</span><span class="so-metric-label">Pageviews</span></div>
          <div class="so-metric"><span class="so-metric-val">{{ ga.metrics.bounce_rate != null ? ga.metrics.bounce_rate + '%' : '—' }}</span><span class="so-metric-label">Bounce rate</span></div>
        </div>
        <div v-if="ga.channels?.length" class="so-channels">
          <div class="so-channels-label">Top channels</div>
          <div v-for="c in ga.channels" :key="c.channel" class="so-channel">
            <span class="so-channel-name">{{ c.channel }}</span>
            <div class="so-channel-bar"><div class="so-channel-fill" :style="{ width: channelPct(c.sessions) + '%' }"></div></div>
            <span class="so-channel-val">{{ fmt(c.sessions) }}</span>
          </div>
        </div>
      </template>
      <div v-else class="so-empty">
        <p class="so-muted">{{ ga?.reason || 'GA4 is not connected for this site.' }}</p>
        <p v-if="ga?.status === 'not_connected'" class="so-hint">Connect a GA4 property under Analytics &amp; Tracking below to light this up.</p>
      </div>
    </section>

    <!-- Form submissions -->
    <section class="so-panel">
      <div class="so-panel-head">
        <div class="so-panel-label">Form submissions</div>
        <router-link :to="{ name: 'submissions', params: { id: siteId } }" class="so-link">View all →</router-link>
      </div>
      <p v-if="subsLoading" class="so-muted">Loading submissions…</p>
      <template v-else>
        <div class="so-subs-total"><span class="so-metric-val">{{ fmt(subsTotal) }}</span><span class="so-metric-label">total submissions</span></div>
        <div v-if="submissions.length" class="so-subs-list">
          <div v-for="s in submissions" :key="s.id" class="so-sub-row">
            <span class="so-pill" :data-status="s.status">{{ s.status }}</span>
            <span class="so-sub-form">{{ s.formIdentifier || s.pageIdentifier || 'form' }}</span>
            <span class="so-sub-fields">{{ s.payloadSummary?.fieldCount || 0 }} fields</span>
            <span class="so-sub-time">{{ fmtDate(s.receivedAt) }}</span>
          </div>
        </div>
        <p v-else class="so-muted">No submissions yet.</p>
      </template>
    </section>

    <!-- History (site commits) -->
    <section class="so-panel so-full">
      <div class="so-panel-head">
        <div class="so-panel-label">History</div>
        <button class="so-link so-link-btn" :disabled="commitsLoading" @click="loadCommits">{{ commitsLoading ? 'Loading…' : 'Refresh' }}</button>
      </div>
      <p v-if="commitsError" class="so-muted">{{ commitsError }}</p>
      <p v-else-if="commitsLoading && !commits.length" class="so-muted">Loading history…</p>
      <p v-else-if="!commits.length" class="so-muted">No history yet.</p>
      <div v-else class="so-commits">
        <div v-for="c in commits.slice(0, 12)" :key="c.sha || c.hash" class="so-commit">
          <code class="so-commit-sha">{{ (c.sha || c.hash || '').slice(0, 7) }}</code>
          <span class="so-commit-msg">{{ c.message || c.subject || 'Change' }}</span>
          <span class="so-commit-time">{{ fmtDate(c.date || c.committed_at || c.created_at) }}</span>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import api from '../utils/api'
import TrafficCard from './TrafficCard.vue'

const props = defineProps({ siteId: { type: String, required: true } })

const ga = ref(null)
const submissions = ref([])
const subsTotal = ref(0)
const commits = ref([])
const gaLoading = ref(false)
const subsLoading = ref(false)
const commitsLoading = ref(false)
const commitsError = ref('')

const fmt = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString())
const fmtDate = (v) => (v ? new Date(v).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—')

let channelMax = 1
const channelPct = (v) => Math.round(((v || 0) / channelMax) * 100)

async function loadGa(id) {
  gaLoading.value = true
  try {
    const { data } = await api.get(`/sites/${id}/analytics/google-analytics/overview`, { params: { days: 30 } })
    ga.value = data.ga4
    channelMax = Math.max(1, ...((ga.value?.channels) || []).map((c) => c.sessions || 0))
  } catch (_) { ga.value = { status: 'unavailable', reason: 'Failed to load GA4' } } finally { gaLoading.value = false }
}
async function loadSubs(id) {
  subsLoading.value = true
  try {
    const { data } = await api.get('/form-submissions', { params: { siteId: id, limit: 5 } })
    submissions.value = data.submissions || []
    subsTotal.value = data.pagination?.total ?? submissions.value.length
  } catch (_) { submissions.value = []; subsTotal.value = 0 } finally { subsLoading.value = false }
}
async function loadCommits() {
  commitsLoading.value = true
  commitsError.value = ''
  try {
    const { data } = await api.get(`/sites/${props.siteId}/commits`)
    commits.value = data.commits || []
  } catch (e) { commitsError.value = e?.response?.data?.error || 'Failed to load history' } finally { commitsLoading.value = false }
}

function boot(id) { if (!id) return; loadGa(id); loadSubs(id); loadCommits() }
onMounted(() => boot(props.siteId))
watch(() => props.siteId, (id) => boot(id))
</script>

<style scoped>
.so-wrap { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; align-items: start; }
.so-full { grid-column: 1 / -1; }

.so-panel { background: var(--glass-bg); border: var(--glass-border); border-radius: var(--radius-md); padding: 16px 18px; backdrop-filter: var(--glass-blur); -webkit-backdrop-filter: var(--glass-blur); box-shadow: var(--elev-sm); color: var(--text-primary); }
.so-panel-head { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; margin-bottom: 14px; }
.so-panel-label { font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.08em; color: var(--accent-light); }
.so-panel-sub { font-size: 0.74rem; color: var(--text-muted); }

.so-muted { color: var(--text-secondary); font-size: 0.85rem; margin: 4px 0; }
.so-hint { font-size: 0.8rem; color: var(--text-secondary); margin: 8px 0 0; }
.so-link { color: var(--accent-light); text-decoration: none; font-size: 0.82rem; background: none; border: none; cursor: pointer; }
.so-link:hover { text-decoration: underline; }
.so-link-btn:disabled { opacity: 0.5; cursor: default; }

.so-metrics-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(72px, 1fr)); gap: 12px; }
.so-metric { display: flex; flex-direction: column; gap: 2px; }
.so-metric-val { font-size: 1.35rem; font-weight: 700; font-variant-numeric: tabular-nums; line-height: 1.1; }
.so-metric-label { font-size: 0.7rem; color: var(--text-secondary); }

.so-channels { margin-top: 16px; display: flex; flex-direction: column; gap: 8px; }
.so-channels-label { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.06em; color: var(--text-muted); }
.so-channel { display: grid; grid-template-columns: 96px 1fr 48px; align-items: center; gap: 10px; font-size: 0.8rem; }
.so-channel-name { color: var(--text-secondary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.so-channel-bar { height: 6px; border-radius: 3px; background: rgba(255,255,255,0.08); overflow: hidden; }
.so-channel-fill { height: 100%; background: var(--accent); border-radius: 3px; }
.so-channel-val { text-align: right; font-variant-numeric: tabular-nums; color: var(--text-primary); }

.so-subs-total { display: flex; align-items: baseline; gap: 8px; margin-bottom: 14px; }
.so-subs-list { display: flex; flex-direction: column; gap: 8px; }
.so-sub-row { display: grid; grid-template-columns: auto 1fr auto auto; align-items: center; gap: 12px; padding: 8px 0; border-top: var(--glass-border); font-size: 0.82rem; }
.so-sub-form { color: var(--text-primary); font-weight: 500; }
.so-sub-fields { color: var(--text-secondary); font-size: 0.76rem; }
.so-sub-time { color: var(--text-muted); font-size: 0.76rem; white-space: nowrap; }
.so-pill { font-size: 0.66rem; font-weight: 700; border-radius: 999px; padding: 2px 9px; background: var(--glass-bg-active); color: var(--text-secondary); text-transform: capitalize; }
.so-pill[data-status='new'], .so-pill[data-status='received'] { background: rgba(56,189,248,0.18); color: #93c5fd; }
.so-pill[data-status='read'], .so-pill[data-status='handled'] { background: rgba(52,211,153,0.18); color: var(--success, #34d399); }
.so-pill[data-status='spam'] { background: rgba(248,113,113,0.18); color: var(--danger, #f87171); }

.so-commits { display: flex; flex-direction: column; }
.so-commit { display: grid; grid-template-columns: 64px 1fr auto; align-items: center; gap: 12px; padding: 8px 0; border-top: var(--glass-border); font-size: 0.82rem; }
.so-commit-sha { font-family: ui-monospace, SFMono-Regular, monospace; font-size: 0.74rem; color: var(--accent-light); }
.so-commit-msg { color: var(--text-primary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.so-commit-time { color: var(--text-muted); font-size: 0.76rem; white-space: nowrap; }

.so-empty { padding: 4px 0; }

@media (max-width: 820px) { .so-wrap { grid-template-columns: 1fr; } }
</style>
